/* Parcours list page */
const ParcoursListPage = () => {
  const D = window.PA_DATA;
  const [view, setView] = useState("grid"); // grid | map
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("Au plus proche");

  // Read filters from URL (?search=… & ?region=…)
  const searchParams = new URLSearchParams(window.location.search);
  const searchQuery = searchParams.get("search") || "";
  const regionFilter = searchParams.get("region") || "";

  // Region matching: compare with parcours region + its commune's region (via D.communes lookup).
  const matchesRegion = (p) => {
    if (!regionFilter) return true;
    if (p.region && p.region.toLowerCase().includes(regionFilter.toLowerCase())) return true;
    return (p.communes || []).some((name) => {
      const commune = D.communes.find((c) => c.name === name);
      return commune && commune.region === regionFilter;
    });
  };

  // Helper function to check if any artist matching the query exists for a parcours
  const hasMatchingArtist = (artistIds) => {
    if (!searchQuery) return true;
    const lowerQuery = searchQuery.toLowerCase();
    return artistIds.some(artistId => {
      const artist = D.artists.find(a => a.id === artistId);
      return artist && (
        artist.name.toLowerCase().includes(lowerQuery) ||
        (artist.commune && artist.commune.toLowerCase().includes(lowerQuery))
      );
    });
  };

  const visibleParcours = D.parcours.filter(p => p.status !== "past");
  const filtered = visibleParcours.filter(p =>
    (status === "all" || p.status === status) &&
    matchesRegion(p) &&
    (
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.communes.some(c => c.toLowerCase().includes(searchQuery.toLowerCase())) ||
      hasMatchingArtist(p.artistIds)
    )
  );

  // Sort the filtered list. Default: soonest happening first (nearest → furthest).
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "A → Z") return a.title.localeCompare(b.title, "fr");
    if (sort === "Plus d'artistes") return (b.artistCount || 0) - (a.artistCount || 0);
    // "Au plus proche" — by start date ascending; missing dates go last.
    const da = a.startISO || "9999-12-31";
    const db = b.startISO || "9999-12-31";
    return da.localeCompare(db);
  });

  const clearRegion = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("region");
    window.location.href = url.pathname + (url.search || "");
  };

  return (
    <div data-screen-label="02 Parcours — Liste">
      {/* Header */}
      <section className="container-w" style={{paddingTop:64, paddingBottom:32}}>
        <div className="row between wrap" style={{gap:32, alignItems:"flex-end"}}>
          <div className="stack gap-4" style={{maxWidth:780}}>
            {searchQuery && (
              <>
                <span className="eyebrow">Résultats de recherche</span>
                <h1 style={{fontSize:"clamp(56px, 7vw, 112px)"}}>
                  <span className="serif-it">{searchQuery}</span>
                </h1>
                <p className="muted" style={{fontSize:18, maxWidth:620, margin:0}}>
                  {filtered.length} {filtered.length > 1 ? "parcours trouvé" : "parcours trouvés"} correspondant à votre recherche.
                </p>
              </>
            )}
            {!searchQuery && (
              <>
                <span className="eyebrow">Édition 2026</span>
                <h1 style={{fontSize:"clamp(56px, 7vw, 112px)"}}>
                  Le parcours,
                  <br/>
                  <span className="serif-it">en un coup d'œil.</span>
                </h1>
                <p className="muted" style={{fontSize:18, maxWidth:620, margin:0}}>
                  L'édition 2026 du parcours d'artistes.
                </p>
              </>
            )}
          </div>
          <div className="stack gap-3" style={{alignItems:"flex-end"}}>
            <div className="row gap-6">
              <Stat n={searchQuery ? filtered.length : visibleParcours.length} label={searchQuery ? "résultat" : "parcours"}/>
              <Stat n={visibleParcours.reduce((s,p)=>s+p.artistCount, 0)} label="artistes"/>
              <Stat n={visibleParcours.reduce((s,p)=>s+p.venueCount, 0)} label="lieux"/>
            </div>
          </div>
        </div>
      </section>

      {/* Filters bar */}
      <section className="container-w" style={{paddingBottom:24}}>
        <div className="card row between wrap" style={{padding:18, gap:18, borderRadius:0}}>
          <div className="row gap-3 wrap">
            <ChipGroup
              items={[
                {value:"all",  label:"Tous"},
                {value:"live", label:"En cours"},
                {value:"soon", label:"À venir"}
              ]}
              value={status}
              onChange={setStatus}
            />
          </div>
          <div className="row gap-3">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                padding:"9px 32px 9px 14px",
                border:"1px solid var(--ink)",
                borderRadius:999,
                background:"var(--paper-warm)",
                fontFamily:"var(--font-body)",
                fontSize:13,
                fontWeight:600,
                color:"var(--ink)",
                cursor:"pointer",
                appearance:"auto"
              }}
            >
              <option value="Au plus proche">Au plus proche</option>
              <option value="A → Z">A → Z</option>
              <option value="Plus d'artistes">Plus d'artistes</option>
            </select>
            <div className="row gap-1" style={{background:"var(--paper)", padding:4, borderRadius:999, border:"1px solid var(--line)"}}>
              {[
                ["grid", "grid"],
                ["list", "list"]
              ].map(([v,icon]) => (
                <button key={v} onClick={() => setView(v)}
                  className={view===v ? "chip-active" : ""}
                  style={{
                    padding:"8px 12px", borderRadius:999,
                    background: view===v ? "var(--ink)" : "transparent",
                    color: view===v ? "var(--paper)" : "var(--ink-soft)"
                  }}>
                  <Icon name={icon} size={14}/>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Active-region chip */}
      {regionFilter && (
        <section className="container-w" style={{paddingBottom:8}}>
          <span className="row gap-2" style={{
            display:"inline-flex", alignItems:"center",
            background:"var(--pink)", color:"var(--ink)",
            padding:"6px 14px", borderRadius:999,
            fontFamily:"var(--font-body)", fontWeight:700, fontSize:13
          }}>
            Région : {regionFilter}
            <button type="button" onClick={clearRegion}
              style={{background:"none", border:"none", cursor:"pointer", padding:"0 0 0 4px", fontSize:16, color:"var(--ink)"}}
              aria-label="Retirer le filtre région">
              ×
            </button>
          </span>
        </section>
      )}

      {/* Result counter */}
      <section className="container-w" style={{paddingBottom:16}}>
        <div className="row between">
          <div className="mono muted">{filtered.length} parcours · {filtered.reduce((s,p)=>s+p.artistCount, 0)} artistes participants</div>
          <div className="row gap-2 mono muted" style={{fontSize:13}}>
            <Icon name="map" size={14}/> Carte indisponible pour le moment
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-w" style={{paddingBottom:80}}>
        {view === "grid" ? (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:24}}>
            {sorted.map(p => <ParcoursCard key={p.id} p={p}/>)}
          </div>
        ) : (
          <div className="card stack" style={{padding:0, overflow:"hidden"}}>
            {sorted.map((p,i) => (
              <a key={p.id} href={`/parcours/${p.slug}`}
                 className="row" style={{
                   padding:"24px 28px", gap:24,
                   borderTop: i>0 ? "1px solid var(--line-soft)" : "none",
                   transition:"background .2s"
                 }}>
                <div className="ph" style={{width:140, height:96, borderRadius:14, flexShrink:0}}>
                  {p.image && (
                    <img src={p.image} alt="" loading="lazy"
                         style={{width:"100%", height:"100%", objectFit:"cover", display:"block"}}
                         onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                  )}
                </div>
                <div className="stack gap-2" style={{flex:1}}>
                  {p.dates && (
                    <div className="row gap-3"><StatusDot status={p.status}/><span className="mono muted">{p.dates}</span></div>
                  )}
                  <div className="serif" style={{fontSize:26, lineHeight:1.1}}>{p.title}</div>
                  <div className="muted clamp-2" style={{fontSize:14}}>{p.summary}</div>
                </div>
                <div className="stack" style={{textAlign:"right", minWidth:180}}>
                  <div className="mono muted">{p.region}</div>
                  <div className="row gap-4 mono" style={{justifyContent:"flex-end", marginTop:6}}>
                    <span>{p.artistCount} artistes</span>
                    <span>{p.venueCount} lieux</span>
                  </div>
                </div>
                <Icon name="arrow-up-right" size={20}/>
              </a>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

/* Tiny dropdown — visually styled chip */
const Dropdown = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{position:"relative"}}>
      <button className="chip" onClick={() => setOpen(o=>!o)} style={{padding:"6px 14px"}}>
        {value} <span style={{fontSize:10}}>▾</span>
      </button>
      {open && (
        <div className="card" style={{
          position:"absolute", top:"calc(100% + 8px)", left:0,
          padding:6, minWidth:200, zIndex:20,
          boxShadow:"var(--shadow-l)"
        }}>
          {options.map(o => (
            <button key={o} onClick={() => { onChange(o); setOpen(false); }}
                    className="row gap-2"
                    style={{
                      padding:"10px 12px", width:"100%", textAlign:"left",
                      borderRadius:10, fontSize:13,
                      background: o===value ? "var(--paper-2)" : "transparent"
                    }}>
              {o===value && <Icon name="check" size={12}/>}
              <span style={{marginLeft: o===value ? 0 : 18}}>{o}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

window.ParcoursListPage = ParcoursListPage;
window.Dropdown = Dropdown;
